import {registerBrick, EnumValue, Enum, GlobalProperties, QueryResult, BrickContext, CloudObject} from 'olympe';
import { ReactBrick, useProperty, cssToSxProps, ifNotTransparent, ifNotNull } from '@olympeio/core';
import { useOlympeTheme } from './mui';
import { combineLatestWith, map } from 'rxjs/operators';
import * as React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUITabs from '@mui/material/Tabs';
import MUITab from '@mui/material/Tab';

export default class Tabs extends ReactBrick {

    /**
     * @override
     */
    setupExecution($: BrickContext) {
        return $.observe('Hidden').pipe(combineLatestWith($.observe('Tabs', false).pipe(
            map(tabs => tabs instanceof Enum ? tabs.getValues() : QueryResult.empty())
        )));
    }

    /**
     * @override
     */
    static getReactComponent($: BrickContext) {
        return (props: { values: [hidden: boolean, tabs: QueryResult<CloudObject>] }) => {
            const [hidden, tabs] = props.values;
            const theme = useOlympeTheme($);
            const beforeTabChange = useProperty($, 'Before Tab Change');
            const currentTab = useProperty($, 'Current Tab', true);
            const borderRadius = useProperty($, 'Border Radius', true);
            let bgColor = useProperty($, 'Default Color', true);
            const height = useProperty($, 'Height', true);
            const tabHeight = height ? height - 10 : 30;

            if(bgColor?.toHexString() === '#00000000') {
                bgColor = theme.palette.secondary.main;
            }

            const setCurrentTab = async (_: any, value: string) => {
                const triggerChange = () => {
                    $.set('Previous Tab', currentTab);
                    $.set('Current Tab', value);
                    $.trigger('On Click');
                }

                if (beforeTabChange) {
                    const beforeTabChange$ = $.runner(beforeTabChange);
                    const [ctrlFlow] = beforeTabChange.getInputs();
                    const [allowCtrlFlow, rejectErrorFLow] = beforeTabChange.getOutputs();

                    beforeTabChange$.trigger(ctrlFlow);
                    await beforeTabChange$.waitFor(allowCtrlFlow);

                    const outputFlow = await Promise.race([
                        beforeTabChange$.waitFor(allowCtrlFlow),
                        beforeTabChange$.waitFor(rejectErrorFLow),
                    ]).finally(() => beforeTabChange$.destroy());

                    if (!(outputFlow instanceof Error)) {
                        triggerChange(); // Do not switch tab in case of an error 
                    }
                } else {
                    triggerChange();
                }

            };

            // No tabs specified -> show a placeholder in DRAW
            if (!hidden && tabs.size() === 0 && $.get(GlobalProperties.EDITION, true)) {
                return (
                    <Box sx={{ backgroundColor: 'lightgrey', width: 1, height: 1, overflow: 'hidden' }}>
                        <Typography sx={{ color: 'black', padding: 1 }}>
                            <b>Tabs</b><br/>
                            Specify <code>Tabs</code> with values to use the component.
                        </Typography>
                    </Box>
                );
            }

            // Tabs are provided
            else if (!hidden && tabs.size() > 0) {
                return (
                    <ThemeProvider theme={theme}>
                        <MUITabs
                            value={currentTab || false}
                            onChange={setCurrentTab}
                            sx={{
                                width: 1,
                                minHeight: '10px',
                                height: 1,
                                '.MuiTabs-flexContainer': { height: 1 },
                                '.MuiTabs-indicator': { height: 0 },
                                '.MuiTabs-scroller': { overflow: 'visible !important' },
                                boxSizing: 'border-box',
                                backgroundColor: bgColor,
                                padding: '5px',
                                ...ifNotTransparent('borderColor', useProperty($, 'Border Color', true)),
                                ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                                ...ifNotNull('borderWidth', useProperty($, 'Border Width', true)),
                                ...ifNotTransparent('borderStyle', 'solid', useProperty($, 'Border Color', true)),
                                ...cssToSxProps(useProperty($, 'CSS Property', true)),
                            }}
                        >
                            {tabs.map((tab: CloudObject, index: number) => {
                                const value = tab.get(EnumValue.valueProp);
                                const name = tab.get(EnumValue.nameProp) ?? value;
                                return (<MUITab
                                    label={name}
                                    value={value}
                                    key={index}
                                    sx={{
                                        minHeight: tabHeight+"px",
                                        height: tabHeight+"px",
                                        borderRadius: '5px',
                                        marginRight: '5px',
                                        color: theme.palette.secondary.dark,
                                        "&.Mui-selected": {
                                            backgroundColor: "white",
                                        },
                                        "&:hover": {
                                            backgroundColor: "rgba(0, 0, 0, 0.04)"
                                        },
                                        "&.Mui-selected:hover": {
                                            backgroundColor: "white"
                                        },
                                    }}
                                />);
                            })}
                        </MUITabs>
                    </ThemeProvider>
                );
            }

            // Hidden or runtime with no tabs specified
            return null;
        };
    }
}

registerBrick('01917585d7cec37c36ef', Tabs);
